import { addMonths, format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import {
  AlertCircle,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  FilterX,
  Info,
  Loader2,
  Monitor,
  MoreHorizontal,
  RefreshCcw,
  Search,
  Share2,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";

import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { useCurrentUser } from "@/features/users/hooks/useCurrentUser";

import { DebouncedInput, SelectedUserInfo } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useInvalidateImagingQueries,
  useUserImagingQuery,
  type ImagingStudy,
} from "@/features/imagings/hooks/useImaging";
import { useMedicalReports } from "@/features/imagings/hooks/useMedicalReports";
import { getTokenViewer } from "@/features/imagings/services/imagingService";
import type { MedicalReport } from "@/features/imagings/types/reports";
import type { User as UserType } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";
import { cn } from "@/shared/lib/utils";

// Nuevos Diálogos de Imagenología
import { DeleteStudyDialog } from "@/features/imagings/components/DeleteStudyDialog";
import { DownloadStudyDialog } from "@/features/imagings/components/DownloadStudyDialog";
import { ReportViewer } from "@/features/imagings/components/ReportViewer";
import { ShareDialog } from "@/features/imagings/components/ShareDialog";
import { StudyInfoDialog } from "@/features/imagings/components/StudyInfoDialog";
import type { Imaging } from "@/features/imagings/schemas/imagings";

interface UserImagingsDialogProps {
  user: UserType | null;
  open: boolean;
  onClose: () => void;
}

const MODALITY_CONFIG: Record<string, { color: string; label: string }> = {
  CT: { color: "#f97316", label: "Tomografía Computarizada (CT)" },
  CR: { color: "#0ea5e9", label: "Rayos X (Computed Radiography) (CR)" },
  XA: { color: "#eab308", label: "Angiografía por Rayos X (XA)" },
  MR: { color: "#22c55e", label: "Resonancia Magnética (MR)" },
  US: { color: "#a855f7", label: "Ultrasonido (US)" },
  DX: { color: "#3b82f6", label: "Rayos X Digitales (DX)" },
  PX: { color: "#06b6d4", label: "Radiografía Panorámica (PX)" },
  SC: { color: "#71717a", label: "Cistoscopia (SC)" },
  NM: { color: "#ef4444", label: "Medicina Nuclear (NM)" },
  RF: { color: "#f59e0b", label: "Radio Fluoroscopia (RF)" },
  OT: { color: "#92400e", label: "Densitometría (OT)" },
  ECG: { color: "#84cc16", label: "Electrocardiograma (ECG)" },
  MG: { color: "#ec4899", label: "Mamografía (MG)" },
  BI: { color: "#8b5cf6", label: "Imagen Biomagnética (BI)" },
  DG: { color: "#f3f4f6", label: "Diafanografía (DG)" },
  ES: { color: "#4d7c0f", label: "Endoscopia (ES)" },
  LS: { color: "#dc2626", label: "Escaneo de Superficie Láser (LS)" },
  PT: { color: "#e11d48", label: "PET (Emisión de Positrones) (PT)" },
  RG: { color: "#334155", label: "Imagen Radiográfica (RG)" },
  ST: { color: "#9f1239", label: "SPECT (ST)" },
  TG: { color: "#ea580c", label: "Termografía (TG)" },
  RTIMAGE: { color: "#1d4ed8", label: "Imagen de Radioterapia (RTIMAGE)" },
  RTDOSE: { color: "#facc15", label: "Dosis de Radioterapia (RTDOSE)" },
  RTSTRUCT: { color: "#2dd4bf", label: "Estructura de Radioterapia (RTSTRUCT)" },
  RTP: { color: "#64748b", label: "Plan de Radioterapia (RTP)" },
};

function getModalityConfig(modality?: string) {
  if (!modality) return { color: "#64748b", label: "Desconocido" };
  return (
    MODALITY_CONFIG[modality.toUpperCase()] ?? {
      color: "#64748b",
      label: modality.toUpperCase(),
    }
  );
}

function normalizePatientName(name?: string): string {
  if (!name) return "—";
  // Nombres DICOM vienen en formato "Apellido^Nombre" o "Apellido^Nombre^Segundo"
  return name.replace(/\^/g, " ").trim() || "—";
}

/** Esqueleto de carga para una card */
function StudyCardSkeleton() {
  return (
    <div className="flex border border-border rounded-xl overflow-hidden bg-card animate-pulse">
      <div className="w-36 shrink-0 bg-muted" />
      <div className="flex-1 p-4 space-y-3">
        <div className="h-4 bg-muted rounded w-2/3" />
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-3 bg-muted rounded w-3/4" />
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-6 bg-muted rounded w-20" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Imagen del estudio con fallback y overlay */
function StudyThumbnail({ study, modality }: { study: ImagingStudy; modality?: string }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="relative w-full h-full bg-slate-900 flex items-center justify-center overflow-hidden group">
      {study.url_thubnail && !imgError ? (
        <img
          src={study.url_thubnail}
          alt={`Estudio ${study.patient_name}`}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 text-slate-400 p-3 h-full">
          <Monitor className="h-8 w-8" />
          <span className="text-xs text-center">{modality ?? "Sin imagen"}</span>
        </div>
      )}

      {/* Overlay ojo al hacer hover */}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center cursor-pointer">
        <Eye className="h-6 w-6 text-white" />
      </div>

      {/* Badge count imágenes */}
      {study.number_imagings !== undefined && (
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] rounded px-1.5 py-0.5 z-10">
          {study.number_imagings} imágenes
        </div>
      )}
    </div>
  );
}

/**
 * Mapea un ImagingStudy (resumen) a un objeto Imaging (completo)
 * para compatibilidad con los componentes de modales.
 */
function mapStudyToImaging(study: ImagingStudy): Imaging {
  return {
    ...study,
    uid: (study as any).uid || study.id,
    iuid: study.iuid || "",
    patient_name: study.patient_name || "",
    patient_id: study.patient_id || "",
    patient_gender: study.patient_gender || "O",
    modality: study.modality || "OT",
    accession_number: study.accession_number || "",
    institution_id: (study as any).institution_id || "",
    institution_name: study.institution_name || "",
    referring_physician: study.referring_physician || "",
    description: study.description || "",
    number_imagings: study.number_imagings || 0,
    size: (study as any).size || 0,
    file: (study as any).file || [],
    medical_report: (study as any).medical_report || [],
    datetime: study.date_study ? parseISO(study.date_study) : new Date(),
    url_thubnail: study.url_thubnail || "",
    path: (study as any).path || "",
    operation_id: (study as any).operation_id || "",
    read: [],
    relevant: [],
    body_part: (study as any).body_part || "N/A",
    control: (study as any).control || {
      active: true,
      created_at: new Date(),
      updated_at: new Date(),
    },
    thumbnails: (study as any).thumbnails,
  } as Imaging;
}

/** Card de un estudio individual modernizada */
function StudyCard({
  study,
  canDeleteStudy,
  onRefresh,
}: {
  study: ImagingStudy;
  canDeleteStudy: boolean;
  onRefresh?: () => void;
}) {
  const modality = study.modality?.toUpperCase();
  const config = getModalityConfig(modality);
  const imaging = mapStudyToImaging(study);
  const { data: user } = useCurrentUser();
  const { invalidateAll } = useInvalidateImagingQueries();

  // Estados para diálogos
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  // Extraer IDs de medical_report si existen
  const queryAccession =
    imaging?.medical_report?.[0]?.accession_number || imaging?.accession_number || "";
  const queryInstitution =
    (typeof imaging?.medical_report?.[0]?.institution === "object"
      ? imaging?.medical_report?.[0]?.institution?._id
      : imaging?.medical_report?.[0]?.institution) ||
    imaging?.institution_id ||
    "";

  // Consulta de informes médicos
  const { data: medicalReports } = useMedicalReports(
    imaging.uid,
    queryAccession,
    queryInstitution,
    isReportsOpen || isShareOpen // Solo cargar si se intenta ver o compartir
  );

  // Separar archivos de imagen de informes PDF manuales
  const manualReportsFromFile = (imaging.file || [])
    .filter((f) => {
      const fileName = typeof f === "string" ? f : f?.id_file;
      return fileName?.toLowerCase().endsWith(".pdf");
    })
    .map((f, index) => {
      const fileName = typeof f === "string" ? f : f?.id_file || "informe.pdf";
      return {
        id: typeof f === "object" ? f.id_file : `manual_${imaging.uid}_${index}`,
        filename: fileName.split("/").pop() || "informe.pdf",
        url: typeof f === "object" ? f.url || "" : f,
        size: typeof f === "object" ? f.size || 0 : 0,
        imaging_id: imaging.uid,
        type: "manual" as const,
        patient_name: imaging.patient_name,
        patient_id: imaging.patient_id,
        created_at:
          typeof f === "object" && f.date ? new Date(f.date * 1000) : new Date(),
        modality: imaging.modality,
        body_part: imaging.body_part,
      };
    });

  const reportsList = [
    ...(imaging.medical_report?.length > 0
      ? imaging.medical_report.map((mr: any) => {
          const fetchedReport = medicalReports?.ris?.find(
            (r: any) =>
              r.id === mr._id ||
              (r.accession_number === mr.accession_number &&
                (typeof r.institution === "string"
                  ? r.institution === (mr.institution?._id || mr.institution)
                  : r.institution?._id === (mr.institution?._id || mr.institution)))
          );

          if (fetchedReport) return fetchedReport;

          return {
            id: mr._id || `ris_${imaging.uid}_${mr.accession_number}`,
            accession_number: mr.accession_number || imaging.accession_number,
            institution: mr.institution?.name || imaging.institution_name,
            patient_name: imaging.patient_name,
            patient_id: imaging.patient_id,
            modality: imaging.modality,
            status: "Pendiente",
            type: "ris",
            created_at: new Date(),
          } as any as MedicalReport;
        })
      : []),
    ...manualReportsFromFile,
  ];

  return (
    <>
      <div className="flex flex-col relative rounded-xl overflow-hidden bg-[#0f172a] shadow-md border border-[#1e293b] hover:border-[#334155] transition-all duration-300 group">
        <div
          className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl z-10"
          style={{ backgroundColor: config.color }}
        />
        <div className="flex w-full h-full pl-1">
          {/* Columna Izquierda: Imagen y Thumbnail interactivo más cuadrado */}
          <div
            className="relative aspect-square w-32 sm:w-36 md:w-40 shrink-0 bg-[#020617] flex items-center justify-center overflow-hidden border-r border-[#1e293b] cursor-pointer"
            onClick={async () => {
              try {
                const studyIuid = study.iuid || study.accession_number;
                if (!studyIuid) {
                  toast.error("El estudio no tiene un IUID válido");
                  return;
                }
                const token = await getTokenViewer(studyIuid);
                const url_viewer = `${import.meta.env.VITE_VIEWER_URL}?token=${token}`;
                window.open(url_viewer, "_blank");
              } catch (error) {
                console.error("Error al obtener token para visor:", error);
              }
            }}
          >
            {modality && (
              <span
                className="absolute top-2 left-2 z-10 text-white text-[10px] sm:text-xs font-bold px-2.5 py-0.5 rounded-full shadow-sm max-w-[90%] truncate"
                style={{ backgroundColor: config.color }}
                title={config.label}
              >
                {config.label}
              </span>
            )}
            <div className="w-full h-full transform group-hover:scale-105 transition-transform duration-500">
              <StudyThumbnail study={study} modality={modality} />
            </div>
          </div>

          {/* Columna Derecha: Información y Acciones */}
          <div className="flex-1 p-3.5 sm:p-5 flex flex-col justify-between min-w-0 bg-transparent relative">
            <div className="space-y-4">
              {/* Header: Nombre paciente y fecha */}
              <div className="relative pr-8">
                <h3 className="font-bold text-[15px] sm:text-base text-white uppercase leading-tight truncate mb-1">
                  {normalizePatientName(study.patient_name)}
                </h3>

                <button
                  onClick={() => setIsInfoOpen(true)}
                  className="absolute -top-1 right-0 p-1.5 rounded-full text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                  title="Ver Información del Estudio"
                >
                  <Info className="h-4 w-4" />
                </button>
              </div>

              {/* Grid de Metadatos 2 Columnas estilo Mockup */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-3 mt-2">
                {/* Columna Izquierda */}
                <div className="space-y-3">
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1">
                      Documento
                    </p>
                    <p className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none truncate">
                      {study.patient_id || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1">
                      Orden
                    </p>
                    <p className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none truncate">
                      {study.accession_number || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1">
                      F. Estudio
                    </p>
                    <p className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none truncate">
                      {study.date_only
                        ? format(parseISO(study.date_only), "dd/MM/yyyy")
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1">
                      Institución
                    </p>
                    <p
                      className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none max-w-[120px] sm:max-w-[140px] truncate"
                      title={study.institution_name || ""}
                    >
                      {study.institution_name || "—"}
                    </p>
                  </div>
                </div>

                {/* Columna Derecha */}
                <div className="space-y-3">
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1">
                      Sexo
                    </p>
                    <p className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none truncate">
                      {study.patient_gender || "No Especificado"}
                    </p>
                  </div>
                  <div className="pt-[34px] hidden sm:block">
                    {/* Espacio en blanco simulando la omision de "Medico" para alinear con F. Estudio */}
                  </div>
                  <div>
                    <p className="text-[#64748b] uppercase tracking-wider text-[8px] sm:text-[9px] font-semibold leading-none mb-1 mt-3 sm:mt-0">
                      F. Carga
                    </p>
                    <p className="font-bold text-[#f1f5f9] text-[11px] sm:text-xs leading-none truncate">
                      {study.control?.created_at
                        ? format(new Date(study.control.created_at * 1000), "dd/MM/yyyy")
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botonera inferior: Funciones primarias inline y secundarias en Action Menu */}
            <div className="flex flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-[#1e293b]">
              {/* Botones Primarios */}
              <div className="flex items-center gap-4 sm:gap-6">
                <button
                  onClick={() => setIsReportsOpen(true)}
                  className="flex items-center gap-1.5
                      text-[10px] sm:text-[11px] font-semibold text-[#14b8a6] hover:text-[#2dd4bf]
                      transition-colors cursor-pointer"
                  title="Ver Informes Médicos"
                >
                  <div className="relative">
                    <FileText className="h-4 w-4 shrink-0" />
                    {reportsList.length > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-emerald-500 text-[8px] font-bold text-white shadow-sm ring-1 ring-[#0f172a]">
                        {reportsList.length}
                      </span>
                    )}
                  </div>
                  <span className="truncate">Informes</span>
                </button>

                <button
                  onClick={() => setIsShareOpen(true)}
                  className="flex items-center gap-1.5
                      text-[10px] sm:text-[11px] font-semibold text-[#a855f7] hover:text-[#c084fc]
                      transition-colors cursor-pointer"
                  title="Compartir Estudio"
                >
                  <Share2 className="h-4 w-4 shrink-0" />
                  <span className="truncate">Compartir</span>
                </button>
              </div>

              {/* Botón de Menú Secundario */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center justify-center p-1.5 rounded-md hover:bg-[#1e293b] text-[#94a3b8] hover:text-white transition-colors"
                    title="Más opciones"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-40 border-[#1e293b] bg-[#0f172a] text-slate-200"
                >
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDownloadOpen(true);
                    }}
                    className="gap-2 cursor-pointer focus:bg-[#1e293b] focus:text-white"
                  >
                    <Download className="h-4 w-4 opacity-70" />
                    <span>Descargar</span>
                  </DropdownMenuItem>

                  {canDeleteStudy && (
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDeleteOpen(true);
                      }}
                      className="gap-2 cursor-pointer text-[#ef4444] focus:bg-[#ef4444]/10 focus:text-[#ef4444]"
                    >
                      <Trash2 className="h-4 w-4 opacity-70" />
                      <span>Eliminar</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Diálogos de Acción */}
      <ShareDialog
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        imaging={imaging}
        reports={reportsList}
        institution={user || undefined}
      />

      <StudyInfoDialog
        isOpen={isInfoOpen}
        onClose={() => setIsInfoOpen(false)}
        imaging={imaging}
      />

      <ReportViewer
        isOpen={isReportsOpen}
        onClose={() => setIsReportsOpen(false)}
        reports={reportsList}
        imaging={imaging}
        onRefresh={onRefresh}
      />

      <DownloadStudyDialog
        isOpen={isDownloadOpen}
        onClose={() => setIsDownloadOpen(false)}
        imaging={imaging}
      />

      <DeleteStudyDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        imaging={imaging}
        onSuccess={() => {
          setIsDeleteOpen(false);
          invalidateAll();
        }}
      />
    </>
  );
}

export function UserImagingsDialog({ user, open, onClose }: UserImagingsDialogProps) {
  const { can } = usePermissions();
  const canDeleteStudy = can("imagings:delete_study");
  const [search, setSearch] = useState("");
  const [date, setDate] = useState<DateRange | undefined>();
  const [modalityFilter, setModalityFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState("");
  const limit = 9; // 9 elementos para grid de 3 columnas

  const hasFilters =
    search.trim() !== "" || modalityFilter !== "all" || date !== undefined;

  const clearFilters = () => {
    setSearch("");
    setModalityFilter("all");
    setDate(undefined);
    setPage(1);
  };

  // Formato date_study: YYYY-MM-DD,YYYY-MM-DD
  const dateStudy =
    date?.from && date?.to
      ? `${format(date.from, "yyyy-MM-dd")},${format(date.to, "yyyy-MM-dd")}`
      : undefined;

  const { data, isLoading, isFetching, error, refetch } = useUserImagingQuery(user?.uid, {
    skip: page,
    limit,
    ...(search.trim().length >= 3 && { q: search.trim() }),
    ...(modalityFilter !== "all" && { modality: modalityFilter }),
    ...(dateStudy && { date_study: dateStudy }),
    ...(refreshKey && { refresh: refreshKey }),
  });

  const studies = data?.data ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.pages ?? 1;

  const handleSearchChange = (val: string) => {
    setSearch(val);
  };

  useEffect(() => {
    setPage(1);
  }, [search]);

  // Limita el rango a máximo 1 mes para evitar consultas demasiado amplias
  const handleDateSelect = (d: DateRange | undefined) => {
    if (d?.from && d?.to) {
      const maxTo = addMonths(d.from, 1);
      setDate({ from: d.from, to: d.to > maxTo ? maxTo : d.to });
    } else {
      setDate(d);
    }
    setPage(1);
  };

  const { invalidateAll } = useInvalidateImagingQueries();

  const handleRefresh = async () => {
    setRefreshKey(Date.now().toString());
    await invalidateAll();
    refetch();
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="fixed top-0 left-0 translate-x-0 translate-y-0 w-screen h-[100dvh] max-w-none sm:max-w-none m-0 p-0 rounded-none! flex flex-col gap-0 overflow-hidden bg-background z-50">
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Monitor className="h-5 w-5 text-teal-600" />
            Imagenología —{" "}
            <span className="font-normal text-muted-foreground">
              {getUserFullName(user)}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Contenido scrollable */}
        <div className="flex-1 overflow-y-auto bg-muted/30">
          <div className="bg-background px-6 pb-4 pt-4 border-b border-border">
            {/* Información Adicional Condicionada */}
            {user &&
              (user.type === "institution" || user.type === "physician") &&
              user.active_imaging === true && (
                <div className="mb-4">
                  <SelectedUserInfo user={user} />
                </div>
              )}

            {/* Filtros — misma estructura que UsersFilters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  {/* Buscador - Expandido para usar más espacio */}
                  <div className="flex-1 w-full min-w-[280px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <DebouncedInput
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Buscar por acceso, CI, nombre..."
                        className="pl-10"
                        debounce={800}
                      />
                    </div>
                  </div>

                  {/* Selectores - Más proporcionales */}
                  <div className="flex flex-wrap items-center gap-3 shrink-0">
                    <Select
                      value={modalityFilter}
                      onValueChange={(val) => {
                        setModalityFilter(val);
                        setPage(1);
                      }}
                    >
                      <SelectTrigger className="w-[240px]">
                        <SelectValue placeholder="Todas las Modalidades" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        <SelectItem value="all">Todas las Modalidades</SelectItem>
                        {Object.entries(MODALITY_CONFIG).map(([key, config]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <div
                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: config.color }}
                              />
                              <span>{config.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          id="date"
                          variant="outline"
                          className={cn(
                            "w-[260px] justify-start text-left font-normal",
                            !date && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                          <span className="truncate">
                            {date?.from ? (
                              date.to ? (
                                <>
                                  {format(date.from, "LLL dd, y", { locale: es })} -{" "}
                                  {format(date.to, "LLL dd, y", { locale: es })}
                                </>
                              ) : (
                                format(date.from, "LLL dd, y", { locale: es })
                              )
                            ) : (
                              "Seleccionar fechas"
                            )}
                          </span>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={date?.from}
                          selected={date}
                          onSelect={handleDateSelect}
                          numberOfMonths={2}
                          locale={es}
                          disabled={(day) => {
                            // Bloquea días > from + 1 mes mientras se selecciona el extremo final
                            if (date?.from && !date?.to) {
                              return day > addMonths(date.from, 1);
                            }
                            return false;
                          }}
                        />
                      </PopoverContent>
                    </Popover>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={isLoading || isFetching}
                      className="h-10 px-3"
                    >
                      <RefreshCcw
                        className={`h-4 w-4 ${isLoading || isFetching ? "animate-spin" : ""}`}
                      />
                      <span className="ml-2 hidden lg:inline">Recargar</span>
                    </Button>

                    {hasFilters && (
                      <Button
                        variant="ghost"
                        onClick={clearFilters}
                        className="text-muted-foreground hover:text-destructive shrink-0 px-2 sm:px-4"
                        title="Limpiar Filtros"
                      >
                        <FilterX className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline-block">Limpiar</span>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="px-6 py-4 space-y-4">
            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                Error al cargar los estudios: {error.message}
              </div>
            )}

            {/* Loading skeletons */}
            {isLoading && (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <StudyCardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Grid de cards — Varias columnas en PC, ajustado para 9 items */}
            {!isLoading && studies.length > 0 && (
              <div className="w-full max-w-[1500px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-max">
                  {studies.map((study) => {
                    // Soportar _id en base al payload que retorna MongoDB internamente aunque el typings engañe.
                    const key =
                      study.id ??
                      study.iuid ??
                      (study as any)._id ??
                      Math.random().toString();
                    return (
                      <StudyCard
                        key={key}
                        study={study}
                        canDeleteStudy={canDeleteStudy}
                        onRefresh={handleRefresh}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Estado vacío */}
            {!isLoading && !error && studies.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                <Monitor className="h-12 w-12 mb-4 opacity-30" />
                <p className="text-sm">
                  {search.trim()
                    ? "No se encontraron estudios para la búsqueda."
                    : "Este usuario no tiene estudios de imagenología registrados."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer con paginación */}
        {pagination && totalPages > 1 && (
          <div className="px-6 py-3 border-t border-border flex items-center justify-between shrink-0 bg-background">
            <p className="text-xs text-muted-foreground">
              {pagination.total.toLocaleString()} estudios · página {page} de {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Loading indicator de página */}
        {isLoading && studies.length > 0 && (
          <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
